/**
 * The notification settings (container, phase 7b item 4; tenant.admin): the
 * rules form and the channels.
 *
 * The rules form is a dirty-aware editor (D35, `useLiveResource` on
 * `alertSettings`): with no unsaved edits another admin's save refreshes it in
 * place; with edits, a banner offers "Overwrite with mine" or "Discard mine
 * and load theirs" (a 409 too). The channels are a live list: add, switch on
 * or off, test and delete act at once, and a channel change never disturbs
 * the form's draft (it does not move the settings' version).
 */
import type { AlertSettingsWire, CreateAlertChannelRequestWire, TestAlertResultWire } from '@acp/api-contract/alerts.js';
import { useState } from 'react';
import { toast } from 'sonner';
import { AlertSettingsConflict } from '../api/alerts.js';
import { useAlertPickers, useAlertSettings, useCreateAlertChannel, useDeleteAlertChannel, useLiveResource, useSaveAlertSettings, useServices, useTestAlert, useUpdateAlertChannel } from '../app/hooks.js';
import { CloudOnlyGate } from './CloudOnlyPage.js';
import { AddChannelDialog } from '../components/alerts/AddChannelDialog.js';
import { AlertChannels } from '../components/alerts/AlertChannels.js';
import { AlertRulesForm } from '../components/alerts/AlertRulesForm.js';
import { LiveUpdateBanner } from '../components/LiveUpdateBanner.js';
import { Button } from '../components/ui/button.js';
import { draftOf, isRulesDirty, type RulesDraft } from '../lib/alerts.js';
import { whenText } from '../lib/live/whenText.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** A cloud-only screen (`RouteDef.hosts`): its calls go to the cloud from a node's page too; "needs a cloud connection" where they cannot. */
export function AlertSettingsView() {
  return (
    <CloudOnlyGate route="getAlertSettings" what="Alert settings" path="/alerts/settings">
      <AlertSettingsViewPage />
    </CloudOnlyGate>
  );
}

function AlertSettingsViewPage() {
  const settings = useAlertSettings();
  if (settings.data !== undefined) return <AlertSettingsPage latest={settings.data} />;
  if (settings.error !== null) {
    return (
      <div className="mx-auto grid max-w-xl gap-4" role="alert" data-testid="alert-settings-failed">
        <p className="text-sm text-destructive">Could not load the notification settings: {messageOf(settings.error)}</p>
        <Button variant="outline" className="w-fit" onClick={() => void settings.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  return <p className="p-8 text-center text-sm text-muted-foreground">Loading the notification settings…</p>;
}

function AlertSettingsPage({ latest }: { readonly latest: AlertSettingsWire }) {
  const { nowMs } = useServices();
  const save = useSaveAlertSettings();
  const createChannel = useCreateAlertChannel();
  const updateChannel = useUpdateAlertChannel();
  const deleteChannel = useDeleteAlertChannel();
  const test = useTestAlert();
  const pickers = useAlertPickers();
  const [loaded] = useState(latest);
  const [draft, setDraft] = useState<RulesDraft>(() => draftOf(loaded));
  const [adding, setAdding] = useState(false);
  const [results, setResults] = useState<readonly TestAlertResultWire[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const live = useLiveResource<AlertSettingsWire>({
    kind: 'alertSettings',
    id: 'settings',
    loaded,
    latest,
    isDirty: (base) => isRulesDirty(draft, base),
    versionOf: (s) => s.version,
    authorOf: (s) => s.updatedBy ?? 'someone',
    writtenAtOf: (s) => (s.updatedAt === null ? 0 : Date.parse(s.updatedAt)),
    onReset: (s) => setDraft(draftOf(s)),
  });

  const submit = (version: number): void => {
    live.beginSave();
    save.mutate(
      { version, ...draft },
      {
        onSuccess: (saved) => {
          live.saved(saved);
          toast.success('Rules saved');
        },
        onError: (err) => {
          if (err instanceof AlertSettingsConflict) live.conflict(err.current);
          else {
            live.saveFailed();
            toast.error(`Not saved: ${messageOf(err)}`);
          }
        },
      },
    );
  };

  const channelAction = (channelId: string, run: () => Promise<unknown>, done: string): void => {
    setBusy(channelId);
    run()
      .then(() => toast.success(done))
      .catch((err: unknown) => toast.error(messageOf(err)))
      .finally(() => setBusy(null));
  };

  const channelError = createChannel.error ?? updateChannel.error ?? deleteChannel.error ?? test.error;
  return (
    <div className="mx-auto grid max-w-6xl gap-6" data-testid="alert-settings-page">
      {live.notice === null ? null : (
        <LiveUpdateBanner
          notice={{ kind: live.notice.kind, who: live.notice.by, at: whenText(live.notice.atMs, nowMs()), what: 'notification settings' }}
          pending={save.isPending}
          canSave
          onOverwrite={() => {
            const theirs = live.keepMine();
            if (theirs !== null) submit(theirs.version);
          }}
          onDiscard={live.discardMine}
        />
      )}
      <AlertRulesForm draft={draft} dirty={isRulesDirty(draft, live.base)} saving={save.isPending} error={null} onChange={setDraft} onSave={() => submit(live.base.version)} />
      <AlertChannels
        channels={latest.channels}
        testResults={results}
        testing={test.isPending}
        busyChannelId={busy}
        error={channelError === null ? null : messageOf(channelError)}
        onAdd={() => {
          createChannel.reset();
          setAdding(true);
        }}
        onTest={(channelId) => {
          test.mutate(channelId, { onSuccess: (r) => setResults(r) });
        }}
        onToggle={(channelId, enabled) => channelAction(channelId, () => updateChannel.mutateAsync({ channelId, change: { enabled } }), enabled ? 'Channel on' : 'Channel off')}
        onDelete={(channelId) => channelAction(channelId, () => deleteChannel.mutateAsync(channelId), 'Channel deleted')}
      />
      <AddChannelDialog
        open={adding}
        onOpenChange={setAdding}
        groups={pickers.groups.data ?? []}
        sites={pickers.sites.data ?? []}
        pending={createChannel.isPending}
        error={createChannel.error === null ? null : messageOf(createChannel.error)}
        onSubmit={(request: CreateAlertChannelRequestWire) => {
          createChannel.mutate(request, {
            onSuccess: (channel) => {
              setAdding(false);
              toast.success(`Added “${channel.name}”`);
            },
          });
        }}
      />
    </div>
  );
}
